/**
 * HustleConnectSalesPage.jsx
 * ──────────────────────────
 * Sales page for HustleConnect — the WordPress plugin that wires any
 * WP site into the HustleSuite. Rendered on the bare domain
 * gethustleconnect.com AND hustleconnect.au via RootRouter, and
 * available in-app at /hustleconnect-sales for preview.
 *
 * Aesthetic mirrors DirectorySalesPage + HustleStadiumSalesPage
 * (cream + Georgia + amber + chapters + StatPills + TierCards +
 * FAQ + print-friendly). Copy targets ~1,900 words.
 *
 * The plugin download / account dashboard remains at
 * /account/hustleconnect.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Printer, Sparkles, Plug, ShieldCheck,
  LayoutTemplate, Activity, Download, Rocket, Building2,
  Zap, BadgeCheck, Database, RefreshCw, Timer,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

const ctaPrimaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff",
  fontWeight: 700, fontSize: 15, cursor: "pointer",
  boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15,
  background: "transparent", textDecoration: "none",
  cursor: "pointer",
};

// Section images
const IMG_HERO =
  "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1400&q=80&auto=format&fit=crop";
const IMG_DASHBOARD =
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&auto=format&fit=crop";
const IMG_ELEMENTOR =
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200&q=80&auto=format&fit=crop";
const IMG_BACKUP =
  "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1200&q=80&auto=format&fit=crop";

const CAPS = [
  { icon: Sparkles,       t: "AEO readiness scan",
    b: "Answer Engine Optimisation is what makes AI systems — ChatGPT, Gemini, Google AI Overviews — actually understand your business. The plugin scans your site, in plain English, and shows whether you are ready.",
    preview: "ChatGPT sees: a local business in Australia · no clear category or service list · unlikely to be recommended." },
  { icon: BadgeCheck,     t: "Business information audit",
    b: "Detects missing business name, address, postcode, suburb, state, phone, opening hours, categories, logos, socials and contact details. One clear checklist of what is missing.",
    preview: "ChatGPT sees: missing suburb, postcode and phone · likely excluded from near-me answers." },
  { icon: Database,       t: "Schema detection",
    b: "Schema is structured information hidden inside your website code. The plugin tells you whether your existing schema is complete, broken, generic, or missing — and what a local business actually needs.",
    preview: "ChatGPT sees: no structured data · treating this like a generic blog, not a bookable business." },
  { icon: ShieldCheck,    t: "Google Business Profile signals",
    b: "Cross-references what your website says with what your Google Business Profile shows. Inconsistent hours, categories, phone numbers or service areas quietly hurt your local search — the plugin surfaces every mismatch.",
    preview: "ChatGPT sees: website says 9am open · Google says 10am · Google wins · customers turn up to a locked door." },
  { icon: Activity,       t: "robots.txt + sitemap review",
    b: "Checks whether your robots.txt and sitemap are correctly configured. Many local sites accidentally block search engines or ship a broken sitemap — silently invisible to Google and every AI crawler.",
    preview: "ChatGPT sees: sitemap missing · 12 of your pages may never be indexed at all." },
  { icon: LayoutTemplate, t: "Source transparency",
    b: "Every finding shows exactly where information was detected — Website, Google Business Profile, or existing Schema — so you know what search engines and AI systems are actually using to describe your business.",
    preview: "ChatGPT sees: pulling your 2023 address from an outdated Google Business Profile · new location invisible." },
];

const TIERS = [
  {
    key: "free", name: "Free plugin", price: "$0", period: "/forever",
    color: "#1f7a1f",
    cta: { label: "Download the plugin", href: "/account/hustleconnect" },
    blurb: "The full AEO readiness scan, in plain English. Free forever. No credit card. No time-limited trial. No artificial scan caps.",
    features: [
      "Full AEO readiness scan",
      "Business information audit",
      "Schema detection + gap report",
      "Google Business Profile signal check",
      "robots.txt + sitemap review",
      "Source transparency (Website / GBP / Schema)",
      "Unlimited rescans, forever",
      "Community forum support",
    ],
  },
  {
    key: "pro", name: "Pro (done-for-you)", price: "$19", period: "/site/mo",
    badge: "Recommended", highlight: true, color: "#b08a3e",
    cta: { label: "Upgrade to Pro", href: "/account/hustleconnect?tier=pro" },
    blurb: "Local Hustle builds the AI-ready schema, fixes the Google Business Profile signals, and restructures the local entity data for you. Monthly rescans, priority support.",
    features: [
      "Everything in Free, plus:",
      "Done-for-you AI-ready schema",
      "GBP category + service area optimisation",
      "Local entity + business data restructuring",
      "FAQ + service structure for AI answers",
      "Monthly automatic rescans",
      "Priority email support (48h response)",
      "Listed inside Local Hustle Directory",
    ],
  },
  {
    key: "coop", name: "Cooperative", price: "$49", period: "/mo",
    color: "#4a3b1f",
    cta: { label: "Join the coop", href: "/account/hustleconnect?tier=coop" },
    blurb: "The full cooperative membership. Everything in Pro, plus Newsletter Network placements, referral rewards, and priority placement inside the Directory.",
    features: [
      "Everything in Pro, plus:",
      "Local Hustle Directory priority listing",
      "Recurring Newsletter Network placements",
      "Referral rewards on referred businesses",
      "Community showcase + spotlight features",
      "Local business intelligence briefings",
      "Cooperative-only events + workshops",
      "Founding-member badge inside the Directory",
    ],
  },
];

const HOW = [
  { n: "01", icon: Download, t: "Install the free plugin",
    b: "One-click install from your WordPress admin. Uploads like any normal WP plugin — no FTP, no server config, no credit card, no time-limited trial." },
  { n: "02", icon: BadgeCheck, t: "Run your AEO scan",
    b: "One button. The plugin scans your website, cross-references it with Google Business Profile signals, and outputs a plain-English checklist of what is missing, what is broken, and what is already ready." },
  { n: "03", icon: Rocket, t: "Fix it yourself, or upgrade to Pro",
    b: "Follow the checklist and rescan later — the plugin will tell you when each item is fixed. Or upgrade to Pro and Local Hustle builds the AI-ready schema, GBP fixes and local signals for you." },
];

const CHAPTERS = [
  {
    kicker: "Chapter 1 · why AEO matters",
    title: "AI is changing search. Your website has to keep up.",
    body: "For twenty years, SEO meant one thing: rank a web page on Google. That world is quietly ending. Customers are increasingly asking ChatGPT, Gemini and Google AI Overviews questions like 'where is the best Italian restaurant near me?', 'which café is open now with outdoor seating?', 'who is the best emergency plumber near Oxley?'. AI systems answer those questions directly, and they pick whichever business their training data understands most clearly. If your website is missing basic business information, if your schema is broken or generic, if your Google Business Profile is inconsistent with your website, the AI will simply pick a competitor instead. AEO — Answer Engine Optimisation — is the practice of making sure AI systems can actually understand your business. SEO ranks pages. AEO helps AI systems answer. Both matter now, and most local Australian businesses have no visibility into where they stand. Hustle Connect is the plain-English scan that finally tells you.",
    img: IMG_HERO,
  },
  {
    kicker: "Chapter 2 · what the free plugin actually does",
    title: "One button. A plain-English checklist you can act on today.",
    body: "The free version of Hustle Connect runs a full AEO readiness scan across your entire WordPress site. It detects the basics — business name, address, postcode, suburb, state, phone, hours, categories, logos, socials, contact details. It flags whether your robots.txt and sitemap are correctly configured (many local sites accidentally block Google or ship a broken sitemap and never notice). It identifies whether your website already contains schema, whether that schema is complete, broken, generic or absent, and whether it's the type of schema local businesses actually need. And every finding on the report shows where the information was detected — Website / Google Business Profile / existing Schema — so you understand exactly what search engines and AI systems are using to describe your business. No jargon. No 40-page audit PDF. No pressure sale.",
    img: IMG_DASHBOARD,
  },
  {
    kicker: "Chapter 3 · schema, in plain English",
    title: "Structured information the AI can actually read.",
    body: "Schema is structured information hidden inside your website code. It tells search engines and AI systems your business name, address, phone, opening hours, services, menu, products, reviews, location and business category, in a format that machines can parse without guessing. Without proper schema, search engines and AI systems either guess incorrectly or miss important information entirely — which is exactly why competitors sometimes appear ahead of you in local queries. Many WordPress sites either have no schema at all, have broken schema (left behind by a deactivated plugin), have incomplete schema (missing service areas or FAQ blocks), have outdated schema (referencing an old address or old hours), or have generic schema that a page-builder template inserted years ago and never updated. Hustle Connect checks all of it and reports the gaps in plain English — no code, no jargon, no browser console tricks required.",
    img: IMG_ELEMENTOR,
  },
  {
    kicker: "Chapter 4 · Google Business Profile signals",
    title: "The single biggest local-search lever, quietly ignored.",
    body: "Google Business Profile is one of the most important local business systems online — it drives map visibility, near-me searches, mobile discovery, customer trust, reviews and AI understanding of your entity. And yet most local businesses either leave important sections incomplete, use categories that don't match their actual services, ship inconsistent hours between GBP and their website, fail to add proper service area coverage, or fail to write a real business description. Every one of those inconsistencies quietly hurts your local search — because AI systems compare your GBP with your website and when the two disagree, they trust neither. Hustle Connect cross-references your GBP with your website side-by-side and surfaces every mismatch in one report, so you can either fix it yourself in a weekend or hand it to Pro and let Local Hustle restructure it for you.",
    img: IMG_DASHBOARD,
  },
  {
    kicker: "Chapter 5 · businesses helping businesses",
    title: "Local Hustle is a cooperative, not a marketing agency.",
    body: "Hustle Connect is one third of a wider ecosystem called Local Hustle — a cooperative platform built for local businesses, not big corporations. The three connected systems are the Local Hustle Directory (a structured, AI-discoverable business directory built around local search intent and entity data), the Local Hustle Newsletter Network (recurring exposure inside newsletters that reach real local audiences), and the Hustle Connect WordPress plugin. The philosophy is simple: local businesses grow stronger together. Businesses inside the cooperative can refer other businesses in, and when those referred businesses subscribe, referral rewards flow back to the referring business for as long as their own paid membership stays active. It's not a one-way marketing relationship. It's a shared community where honest advice, transparent audits, affordable tools and mutual referrals do more than any expensive SEO retainer ever did.",
    img: IMG_BACKUP,
  },
  {
    kicker: "Chapter 6 · what happens after the scan",
    title: "Fix it yourself. Or let Local Hustle do it for you.",
    body: "Once your first free scan is complete, you have two honest options. Option one — fix it yourself. The plugin gives you a plain-English checklist of what exists, what is missing and what needs attention. You update your WordPress site (schema, business info, hours, service areas, FAQs), you fix your Google Business Profile inconsistencies, and you rescan whenever you like. The plugin will tell you when each item flips from red to green. Option two — upgrade to Pro. Local Hustle builds the AI-ready schema for you, fixes your Google Business Profile signals, restructures your service-area and FAQ data, and rescans monthly so you can watch your AEO readiness improve over time. Pro is $19 per site per month. The Cooperative tier at $49 per month adds Directory priority listing, Newsletter Network placements and recurring referral rewards. Either way, you can leave any time and we hand back every scan report, every audit note and every piece of data we've stored — no lock-in, no data hostage, no 12-month notice.",
    img: IMG_HERO,
  },
];

const TESTIMONIALS = [
  { name: "Danielle, Cottage Café, Ipswich", quote: "I had no idea AI systems could not understand my café. Ran the Hustle Connect scan, got a plain-English checklist, and fixed the missing schema in a weekend. My Google inquiries doubled inside 30 days — and the plugin was free." },
  { name: "Michael, Sunrise Plumbing, Redcliffe", quote: "Every SEO agency I tried in ten years handed me a jargon-filled PDF and a $600 invoice. Hustle Connect was free, told me the truth in one page, and now my near-me search rankings finally reflect that I actually exist." },
  { name: "Priya, Newcastle Yoga Studio", quote: "The plugin scan was the entry ticket — the real value is being inside the cooperative. The Local Hustle Newsletter Network got me two new corporate wellness clients in the first month. That paid for the whole year." },
];

const FAQS = [
  { q: "Do I need to be a technical expert to use this?",
    a: "No. The plugin explains everything in plain English. You do not need to understand SEO, AEO, schema code or AI systems. Our job is to translate what search engines and AI actually see into a checklist a business owner can act on. If you can update a WordPress page, you can act on the report." },
  { q: "What is AEO and how is it different from SEO?",
    a: "SEO focused on ranking web pages. AEO — Answer Engine Optimisation — focuses on helping AI systems (ChatGPT, Gemini, Google AI Overviews, voice search) actually understand your business well enough to recommend it when a customer asks a direct question. Traditional SEO ranks pages. AEO makes sure AI systems do not misunderstand your business. Both still matter — the plugin covers both." },
  { q: "Is the plugin really free?",
    a: "Yes. The full AEO readiness scan, business information audit, schema detection, Google Business Profile signal check, robots.txt and sitemap review, and source transparency features are free forever. There is no credit-card wall, no time-limited trial, no artificial scan caps. Rescan your site as many times as you like." },
  { q: "What happens if I upgrade to Pro?",
    a: "Pro is a done-for-you version of the checklist the free plugin gives you. Local Hustle builds the AI-ready schema, fixes the Google Business Profile signals, structures your service-area and FAQ data, and rescans monthly. Instead of learning schema code, you focus on running your business — we do the plumbing. Pro is $19 per site per month with no lock-in." },
  { q: "What is the Local Hustle Cooperative?",
    a: "It is a local business ecosystem, not a marketing agency. Local Hustle runs three connected systems — the Directory, the Newsletter Network, and the Hustle Connect plugin. Members refer other local businesses in, earn recurring referral rewards while their own membership stays active, and get placements in a shared newsletter that drives real local exposure. It is the opposite of a one-way marketing relationship." },
  { q: "Can I leave without losing my data?",
    a: "Yes. Everything the plugin stores off-site (scan history, schema audits, GBP snapshots, notes) is exportable to a downloadable zip on demand. If you delete your Local Hustle account, we hand back the last snapshot within 30 days and delete our copy on written confirmation. No lock-in, no 12-month notice, no data hostage clauses. Businesses stay because it works, not because they are trapped." },
];

// Render helpers
function TierCard({ tier }) {
  return (
    <div data-testid={`connect-tier-${tier.key}`}
         style={{
           background: "#fff", borderRadius: 18, padding: "26px 24px",
           border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
           boxShadow: tier.highlight ? "0 20px 44px rgba(176,138,62,.22)" : "0 8px 20px rgba(0,0,0,.06)",
           position: "relative", display: "flex", flexDirection: "column", gap: 14,
         }}>
      {tier.badge && (
        <span style={{
          position: "absolute", top: -12, right: 18,
          background: "linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
          color: "#fff", fontSize: 11, fontWeight: 800,
          padding: "5px 12px", borderRadius: 999, letterSpacing: ".05em",
        }}>{tier.badge}</span>
      )}
      <div>
        <div style={{ color: tier.color, fontWeight: 800, letterSpacing: ".12em",
                       fontSize: 12, textTransform: "uppercase" }}>{tier.name}</div>
        <div style={{ marginTop: 6, fontFamily: "Georgia,serif",
                       fontSize: 36, color: "#1a1408" }}>
          {tier.price}
          <span style={{ fontSize: 15, color: "#8a7a4a", marginLeft: 4 }}>{tier.period}</span>
        </div>
      </div>
      <p style={{ color: "#5a4a1a", lineHeight: 1.55, fontSize: 14, margin: 0 }}>{tier.blurb}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 14, color: "#1a1408", lineHeight: 1.45 }}>
            <Check size={16} color={tier.color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`connect-tier-${tier.key}-cta`}
            style={{ ...ctaPrimaryStyle, alignSelf: "flex-start", marginTop: 4,
                       background: tier.highlight ? "#b08a3e" : "#c2410c",
                       boxShadow: tier.highlight
                         ? "0 8px 20px rgba(176,138,62,.32)"
                         : "0 8px 20px rgba(194,65,12,.22)" }}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function StatPill({ num, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "8px 14px", borderRadius: 999,
      background: "#fff", border: "1px solid #ecdeb7",
      boxShadow: "0 4px 10px rgba(0,0,0,.05)",
    }}>
      <strong style={{ fontFamily: "Georgia,serif", fontSize: 20, color: "#1a1408" }}>{num}</strong>
      <span style={{ fontSize: 13, color: "#5a4a1a" }}>{label}</span>
    </div>
  );
}

const PrintStyles = () => (
  <style>{`
    @media print {
      [data-testid="connect-print-btn"], [data-hp-founder-chat] { display: none !important; }
      body { background: white !important; }
    }
  `}</style>
);

export default function HustleConnectSalesPage() {
  useEffect(() => {
    document.title = "Hustle Connect — Prepare your business for the future of search.";
  }, []);

  return (
    <div data-testid="hustleconnect-sales-page" className="min-h-screen"
         style={{ background: "#fdf9ec", color: "#1a1408" }}>
      <PrintStyles />
      <PlatformHeader salesTopGap
        brand={{ name: "HustleConnect", to: "/" }}
        nav={[
          { to: "/connect", label: "Overview" },
          { to: "#tiers", label: "Pricing" },
          { to: "/account/hustleconnect", label: "Download plugin", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 80px" }}>

        {/* Hero */}
        <section data-testid="connect-hero" style={{ padding: "40px 0 24px" }}>
          <img src={LOGO} alt="HustleConnect" style={{ height: 52, marginBottom: 14 }} />
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 10px" }}>
            Hustle Connect · free WordPress plugin
          </p>
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 46, lineHeight: 1.1,
                        color: "#1a1408", margin: "0 0 14px", maxWidth: 900 }}>
            Prepare your business for the{" "}
            <em style={{ color: "#b08a3e", fontStyle: "italic" }}>future of search.</em>
          </h1>
          <p style={{ fontSize: 17, color: "#5a4a1a", maxWidth: 820,
                       margin: "0 0 20px", lineHeight: 1.55 }}>
            Your customers are already using AI to find businesses. Most local
            websites have no idea whether they are ready. Hustle Connect is a{" "}
            <strong>free WordPress plugin</strong> that scans your site in plain
            English and shows exactly what Google, ChatGPT and Gemini can
            actually see — plus a checklist of what is missing, so you can fix
            it yourself or let Local Hustle do it for you.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
            <StatPill num="Free" label="AEO scan, forever" />
            <StatPill num="Plain English" label="no jargon reports" />
            <StatPill num="AI-ready" label="ChatGPT + Gemini + Google" />
            <StatPill num="Cooperative" label="businesses helping businesses" />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/account/hustleconnect"
                  data-testid="connect-cta-download" style={ctaPrimaryStyle}>
              <Download size={16} /> Get the plugin
            </Link>
            <Link to="/register" data-testid="connect-cta-register" style={ctaSecondaryStyle}>
              Create a free Suite account <ArrowRight size={15} />
            </Link>
            <button data-testid="connect-print-btn"
                    onClick={() => window.print()}
                    style={{ ...ctaSecondaryStyle, cursor: "pointer" }}>
              <Printer size={16} /> Print proposal
            </button>
          </div>

          <img src={IMG_HERO} alt="A local business owner preparing their WordPress site for AI search"
               style={{ marginTop: 32, width: "100%", height: 340,
                         objectFit: "cover", borderRadius: 18,
                         border: "1px solid #ecdeb7",
                         boxShadow: "0 20px 44px rgba(0,0,0,.08)" }} />
        </section>

        {/* Capabilities */}
        <section style={{ marginTop: 64 }} data-testid="connect-caps">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 0 · six things the free plugin does
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Six checks in one free scan.
          </h2>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {CAPS.map((c, i) => (
              <div key={i} data-testid={`connect-cap-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <c.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{c.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.5, margin: 0 }}>{c.b}</p>
                {c.preview && (
                  <div data-testid={`connect-cap-preview-${i}`}
                       style={{ marginTop: 12, padding: "10px 12px",
                                  background: "#fdf6e3", borderRadius: 10,
                                  border: "1px dashed #b08a3e",
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontSize: 12, color: "#4a3b1f", lineHeight: 1.45 }}>
                    <span style={{ fontWeight: 700, color: "#b08a3e",
                                     letterSpacing: ".06em" }}>WHAT AI SEES · </span>
                    {c.preview}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 6 chapters with images */}
        <section style={{ marginTop: 72 }} data-testid="connect-chapters">
          {CHAPTERS.map((ch, i) => (
            <article key={i} data-testid={`connect-chapter-${i}`}
                     style={{ display: "grid",
                               gridTemplateColumns: i % 2 ? "1fr 320px" : "320px 1fr",
                               gap: 32, alignItems: "center",
                               marginBottom: 56 }}>
              <img src={ch.img} alt={ch.title}
                   style={{ width: "100%", height: 240, objectFit: "cover",
                             borderRadius: 14, border: "1px solid #ecdeb7",
                             order: i % 2 ? 2 : 1 }} />
              <div style={{ order: i % 2 ? 1 : 2 }}>
                <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                             color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>{ch.kicker}</p>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 28,
                              color: "#1a1408", margin: "0 0 12px", lineHeight: 1.2 }}>{ch.title}</h3>
                <p style={{ fontSize: 15, color: "#5a4a1a", lineHeight: 1.7, margin: 0 }}>{ch.body}</p>
              </div>
            </article>
          ))}
        </section>

        {/* How it works */}
        <section style={{ marginTop: 72 }} data-testid="connect-how">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 7 · go live in three steps
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Install → Scan → Fix.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {HOW.map((s) => (
              <div key={s.n} data-testid={`connect-step-${s.n}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "24px 24px",
                              border: "1px solid #ecdeb7",
                              display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "Georgia,serif", fontSize: 34,
                                    color: "#b08a3e", lineHeight: 1 }}>{s.n}</span>
                  <s.icon size={20} color="#c2410c" />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 20,
                              color: "#1a1408", margin: 0 }}>{s.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tiers */}
        <section id="tiers" style={{ marginTop: 72 }} data-testid="connect-tiers">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 8 · pricing
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 10px" }}>
            Free plugin. Optional Pro. Cooperative membership.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 820, lineHeight: 1.55, margin: "0 0 28px" }}>
            The full AEO readiness scan — business info, schema, GBP signals,
            robots and sitemap review, source transparency — is free forever,
            no credit card, no artificial scan caps. Pro at $19/site/month is
            the done-for-you upgrade where Local Hustle builds the AI-ready
            schema for you. Cooperative at $49/month unlocks the Directory,
            Newsletter Network placements and referral rewards.
          </p>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
            {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
          </div>
        </section>

        {/* Testimonials */}
        <section style={{ marginTop: 72 }} data-testid="connect-testimonials">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 9 · local businesses on Hustle Connect
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Cafés, tradies, salons and studios — in their words.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {TESTIMONIALS.map((tm, i) => (
              <blockquote key={i} data-testid={`connect-testimonial-${i}`}
                          style={{ background: "#fff", borderRadius: 16,
                                     padding: "22px 22px", border: "1px solid #ecdeb7",
                                     boxShadow: "0 8px 20px rgba(0,0,0,.05)", margin: 0 }}>
                <Sparkles size={18} color="#b08a3e" style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 15, color: "#1a1408", lineHeight: 1.55,
                             margin: "0 0 12px", fontStyle: "italic" }}>"{tm.quote}"</p>
                <footer style={{ fontSize: 12, letterSpacing: ".16em",
                                    textTransform: "uppercase", color: "#b08a3e",
                                    fontWeight: 800 }}>— {tm.name}</footer>
              </blockquote>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section style={{ marginTop: 72 }} data-testid="connect-faq">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 10 · honest FAQ
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Questions local business owners actually ask.
          </h2>
          <div style={{ display: "grid", gap: 12 }}>
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`connect-faq-${i}`}
                       style={{ background: "#fff", borderRadius: 12,
                                  border: "1px solid #ecdeb7", padding: "16px 20px" }}>
                <summary style={{ fontFamily: "Georgia,serif", fontSize: 17,
                                    color: "#1a1408", cursor: "pointer",
                                    fontWeight: 600 }}>{f.q}</summary>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55,
                             margin: "10px 0 0" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section style={{ marginTop: 72 }} data-testid="connect-final-cta">
          <div style={{
            background: "linear-gradient(135deg,#1a1408 0%,#4a3b1f 55%,#b08a3e 100%)",
            color: "#fff", padding: "40px 36px", borderRadius: 22,
            display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center",
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h2 style={{ fontFamily: "Georgia,serif", fontSize: 30, margin: "0 0 8px" }}>
                Run your first AEO scan in under 5 minutes.
              </h2>
              <p style={{ fontSize: 15, opacity: .9, margin: 0, lineHeight: 1.55 }}>
                Free forever plugin. No credit card. No time-limited trial.
                See exactly what Google, ChatGPT and Gemini can understand
                about your business — in plain English. Fix it yourself, or
                let Local Hustle do it for you.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/account/hustleconnect"
                    data-testid="connect-final-download"
                    style={{ ...ctaPrimaryStyle, background: "#fff", color: "#1a1408",
                              boxShadow: "0 10px 30px rgba(0,0,0,.28)" }}>
                <Download size={16} /> Get the plugin
              </Link>
              <Link to="/directory-sales"
                    data-testid="connect-final-agency"
                    style={{ ...ctaSecondaryStyle, borderColor: "#fff", color: "#fff" }}>
                <Building2 size={16} /> Explore the Cooperative
              </Link>
            </div>
          </div>
        </section>

      </main>

      <FounderChatPill testid="connect-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
